import { useForm } from 'hooks/useForm';
import { TerminalDto } from '../types/dtos';
import { TERMINAL_CREATE_FIELDS_SCHEMA, TERMINAL_INITIAL_FIELDS } from '../constants/terminal-fields';
import { FormEvent, useCallback } from 'react';
import { useAddTerminalMutation } from '../services';
import { useNavigate } from 'react-router-dom';
import { ServerErrorType } from 'types/server-responses';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { useAppDispatch } from 'app/hooks';
import { removeEmpty, trimValues } from 'utils/object-helpers';

const useController = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [addTerminal, { isLoading: isAddTerminalLoading }] = useAddTerminalMutation();
    const { formData, handleChange, handleSubmit, errors } = useForm({
        defaultValues: TERMINAL_INITIAL_FIELDS,
        validationSchema: TERMINAL_CREATE_FIELDS_SCHEMA,
    });

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'terminal.TERMINAL_ADDED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(() => {
        const data = { ...formData, account: { email: formData.email, password: formData.password } };
        delete data.email;
        delete data.password;

        return trimValues(removeEmpty(data)) as TerminalDto;
    }, [formData]);

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                try {
                    const payload = getPayload();
                    await addTerminal(payload).unwrap();
                    handleSuccess();
                    navigate('../terminals');
                } catch (error) {
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [addTerminal, getPayload, handleErrors, handleSubmit, handleSuccess, navigate],
    );

    const onCancel = useCallback(() => {
        navigate('../terminals');
    }, [navigate]);

    return {
        formData,
        onChange: handleChange,
        onSubmit,
        onCancel,
        isAddTerminalLoading,
        errors,
    };
};

export default useController;
